import React, { useState, useRef } from 'react';
import { useCommonSelector } from '@wajeez-web/stores/common';
import { DragAndDropFileUploaderInputProps } from './Types';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';
import InputLabel from './InputLabel';

const DragAndDropFileUploaderInput = ({
  name,
  label,
  required,
  onChange,
  types,
  upTo,
}: DragAndDropFileUploaderInputProps) => {
  const inputref = useRef<HTMLInputElement>(null);
  const [isDraging, setDraging] = useState<boolean>(false);
  const [files, setFiles] = useState<File[]>([]);

  const { lang } = useCommonSelector((state) => state.common.settings);

  const uploadFile = (e: any) => {
    const formData = new FormData();
    // Update the formData object
    formData.append('file', e.target.files[0], e.target.files[0].name);

    onChange(name, formData);

    setFiles(e.target.files);
  };

  // This function will be triggered when dropping
  const dropHandler = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const formData = new FormData();
    if (isDraging) {
      setDraging(false);
    }
    formData.append(
      'file',
      event?.dataTransfer?.files[0],
      event?.dataTransfer?.files[0].name
    );

    onChange(name, formData);
  };

  // This makes the third box become droppable
  const allowDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!isDraging) {
      setDraging(true);
    }
  };

  return (
    <div>
      {label && <InputLabel text={label} name={name} required={required} />}
      <div
        onClick={() => {
          inputref.current?.click();
        }}
        onDragOver={allowDrop}
        onDrop={dropHandler}
        className={`mt-1 cursor-pointer flex justify-center px-6 pt-5 pb-6 border-2  border-${
          isDraging ? 'primary' : 'gray-300'
        } hover:border-primary border-dashed rounded-md`}
      >
        <div className="space-y-1 text-center">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            stroke="currentColor"
            fill="none"
            viewBox="0 0 48 48"
            aria-hidden="true"
          >
            <path
              d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <div className="flex text-sm text-gray-600">
            <label
              htmlFor="file-upload"
              className="relative cursor-pointer bg-white rounded-md font-medium text-primary hover:text-primary-light focus-within:outline-none "
            >
              {files.length === 0 && (
                <span>
                  <ReturnTranslatedText
                    origintext="Upload a file"
                    text={`inputs.placeholders.${'Upload a file'}`}
                  />
                  &nbsp;
                </span>
              )}
              <input
                name={name}
                ref={inputref}
                id="file-upload"
                onChange={uploadFile}
                type="file"
                className="sr-only"
              />
            </label>
            {files.length === 0 && (
              <p className="pl-1">
                <ReturnTranslatedText
                  origintext="or drag and drop"
                  text={`inputs.placeholders.${'or drag and drop'}`}
                />
              </p>
            )}
          </div>
          <div className="text-sm text-gray-600">
            {files.length !== 0 &&
              (lang === 'en' ? (
                <p>
                  {`${files.length} ${
                    files.length === 1 ? 'file has' : 'files have'
                  } been uploaded`}
                </p>
              ) : (
                <p>
                  {`تم رفع ${files.length} ${
                    files.length === 1 ? 'ملف' : 'ملفات'
                  }`}
                </p>
              ))}
          </div>
          <p className="text-xs justify-center flex flex-row text-gray-500">
            {types?.join(' ,')} {upTo && 'up to ' + upTo}
          </p>
        </div>
      </div>
    </div>
  );
};

export default DragAndDropFileUploaderInput;
